<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-icon-wrap">
        <i class="bi bi-key-fill"></i>
      </div>
      <h2 class="auth-title">Reset password</h2>
      <p class="auth-subtitle">
        {{ step === 1 ? "We'll send a reset code to your phone." : "Enter the code and your new password." }}
      </p>

      <div v-if="error" class="auth-error"><i class="bi bi-exclamation-circle mr-2"></i>{{ error }}</div>
      <div v-if="success" class="auth-success"><i class="bi bi-check-circle mr-2"></i>{{ success }}</div>

      <form v-if="step === 1" @submit.prevent="requestCode">
        <div class="auth-field">
          <label class="auth-label">Phone Number</label>
          <input v-model="form.phoneNumber" type="tel" class="auth-input" placeholder="+27 ..." required />
        </div>
        <button type="submit" class="auth-btn" :disabled="loading">
          <span v-if="loading" class="auth-spinner"></span>
          <span v-else>Send Reset Code</span>
        </button>
      </form>

      <form v-else @submit.prevent="resetPassword">
        <div class="auth-field">
          <label class="auth-label">Reset Code (6 digits)</label>
          <input v-model="form.resetCode" class="auth-input auth-otp" maxlength="6" minlength="6" required />
        </div>
        <div class="auth-field">
          <label class="auth-label">New Password</label>
          <input v-model="form.newPassword" type="password" class="auth-input" minlength="8" required placeholder="Minimum 8 characters" />
        </div>
        <button type="submit" class="auth-btn" :disabled="loading">
          <span v-if="loading" class="auth-spinner"></span>
          <span v-else>Reset Password</span>
        </button>
      </form>

      <p class="auth-switch">
        <router-link :to="{ name: 'Login' }">← Back to sign in</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import authApi from '@/api/auth'

export default {
  name: 'ForgotPasswordView',
  data () {
    return {
      step: 1,
      form: { phoneNumber: '', resetCode: '', newPassword: '' },
      loading: false,
      error: null,
      success: null
    }
  },
  methods: {
    async requestCode () {
      this.error = null
      this.loading = true
      try {
        await authApi.forgotPassword({ phoneNumber: this.form.phoneNumber })
        this.success = 'Reset code sent!'
        this.step = 2
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to send reset code.'
      } finally {
        this.loading = false
      }
    },
    async resetPassword () {
      this.error = null
      this.loading = true
      try {
        await authApi.resetPassword(this.form)
        this.success = 'Password reset! Redirecting…'
        setTimeout(() => this.$router.push({ name: 'Login' }), 2000)
      } catch (err) {
        this.error = err.response?.data?.message || 'Reset failed. Check your code and try again.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-page { min-height: 100vh; background: #212121; display: flex; align-items: center; justify-content: center; padding: 20px; }
.auth-card { background: #fff; border-radius: 16px; padding: 40px 36px; width: 100%; max-width: 400px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.auth-icon-wrap { width: 56px; height: 56px; background: rgba(255,152,0,0.12); border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; color: rgb(255,152,0); margin-bottom: 16px; }
.auth-title { font-size: 1.5rem; font-weight: 800; color: #212121; margin: 0 0 4px; }
.auth-subtitle { color: #9e9e9e; font-size: 0.9rem; margin-bottom: 24px; }
.auth-error { background: #fce4ec; border-left: 4px solid #ef5350; color: #b71c1c; border-radius: 8px; padding: 10px 14px; font-size: 0.85rem; margin-bottom: 16px; }
.auth-success { background: #e8f5e9; border-left: 4px solid #66bb6a; color: #1b5e20; border-radius: 8px; padding: 10px 14px; font-size: 0.85rem; margin-bottom: 16px; }
.auth-field { margin-bottom: 16px; }
.auth-label { display: block; font-size: 0.8rem; font-weight: 600; color: #424242; margin-bottom: 6px; }
.auth-input { width: 100%; border: 1.5px solid #e0e0e0; border-radius: 10px; padding: 11px 14px; font-size: 0.9rem; outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: #fafafa; color: #212121; }
.auth-input:focus { border-color: rgb(255,152,0); box-shadow: 0 0 0 3px rgba(255,152,0,0.12); background: #fff; }
.auth-otp { text-align: center; letter-spacing: 12px; font-size: 1.4rem; font-weight: 700; }
.auth-btn { width: 100%; padding: 12px; background: rgb(255,152,0); color: #fff; border: none; border-radius: 10px; font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: background 0.18s, box-shadow 0.18s; display: flex; align-items: center; justify-content: center; gap: 8px; }
.auth-btn:hover:not(:disabled) { background: rgb(230,126,0); box-shadow: 0 4px 16px rgba(255,152,0,0.35); }
.auth-btn:disabled { opacity: 0.65; cursor: not-allowed; }
.auth-spinner { width: 18px; height: 18px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }
.auth-switch { text-align: center; margin-top: 24px; font-size: 0.85rem; color: #9e9e9e; margin-bottom: 0; }
.auth-switch a { color: rgb(255,152,0); font-weight: 600; text-decoration: none; }
.auth-switch a:hover { text-decoration: underline; }
</style>
